import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { colors } from "../utils/theme.js";
import { api } from "../api/client.js";
import { section, inputStyle, primaryBtn, ghostBtn } from "../clients/ui.js";
import { projectTitle } from "../clients/clientLogic.js";
import { PERIOD_OPTIONS, computePeriod, localToday } from "./period.js";
import MeetingView from "./MeetingView.jsx";

const STATUS_LABEL = { draft: "Draft", ready: "Ready", presented: "Presented", sent: "Sent" };
// Fixed 3-letter months: locale "short" months vary by runtime (en-GB gives "Sept").
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const short = (d) => (d ? `${Number(d.slice(8, 10))} ${MONTHS[Number(d.slice(5, 7)) - 1]}` : "");
const projectName = (projects, id) => { const p = projects.find((x) => x.id === id); return p ? projectTitle(p) : null; };

// Update Meetings tab on a client page (spec §3.1): lists and creates Update and Kickoff meetings.
// `projects` is passed only when the client has 2+ live projects: each meeting row then names
// the project it was created under. New meetings attach to `current` (the page's project).
export default function MeetingsTab({ clientId, clientName, users, current, projects = null, objectives, tasks, proposals, flash }) {
  const [meetings, setMeetings] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  // ClientPage recreates flash on every render; read it through a ref so it never re-triggers loads.
  const flashRef = useRef(flash); flashRef.current = flash;

  const load = useCallback(async () => {
    try { const r = await api.meetings.list(clientId); setMeetings(r?.items || []); }
    catch { setMeetings([]); flashRef.current("error", "Couldn’t load meetings."); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  if (openId) {
    return <MeetingView meetingId={openId} clientName={clientName} users={users} current={current} objectives={objectives} tasks={tasks} proposals={proposals} flash={flash}
      onBack={() => { setOpenId(null); load(); }} onDeleted={() => { setOpenId(null); load(); }} />;
  }
  const sorted = [...(meetings || [])].sort((a, b) => (a.meetingDate < b.meetingDate ? 1 : a.meetingDate > b.meetingDate ? -1 : (b.number || 0) - (a.number || 0)));

  return (
    <div style={section}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: colors.textDim, letterSpacing: 1, textTransform: "uppercase" }}>Update meetings</div>
        <button type="button" onClick={() => setCreating(true)} style={primaryBtn}>New meeting</button>
      </div>
      {meetings == null && <div style={{ color: colors.textDim, fontSize: 13 }}>Loading…</div>}
      {meetings && sorted.length === 0 && <div style={{ color: colors.textDim, fontSize: 13 }}>No meetings yet.</div>}
      {sorted.map((m) => (
        <button type="button" key={m.id} onClick={() => setOpenId(m.id)}
          style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 8, border: "none", borderBottom: `1px solid ${colors.border}`, background: "transparent", color: colors.text, cursor: "pointer", textAlign: "left", fontSize: 13 }}>
          <span style={{ fontWeight: 600, flex: 1 }}>Meeting {m.number} · {m.type === "kickoff" ? "Kickoff" : "Update"}
            {projects && m.projectId && projectName(projects, m.projectId) && <span style={{ fontWeight: 400, color: colors.textMuted }}> · {projectName(projects, m.projectId)}</span>}</span>
          <span style={{ color: colors.textMuted }}>{short(m.meetingDate)}</span>
          {m.type !== "kickoff" && <span style={{ color: colors.textDim }}>covers {short(m.periodStart)} – {short(m.periodEnd)}</span>}
          <span style={{ fontSize: 11, fontWeight: 700, color: colors.textMuted, border: `1px solid ${colors.border}`, borderRadius: 20, padding: "2px 8px" }}>{STATUS_LABEL[m.status]}</span>
        </button>
      ))}
      {creating && (
        <NewMeetingDialog clientId={clientId} projectId={current?.id || null} meetings={meetings || []}
          onClose={() => setCreating(false)}
          onCreated={(m) => { setCreating(false); setMeetings((ms) => [...(ms || []), m]); setOpenId(m.id); }} />
      )}
    </div>
  );
}

function NewMeetingDialog({ clientId, projectId, meetings, onClose, onCreated }) {
  const [meetingDate, setMeetingDate] = useState(() => localToday());
  const [type, setType] = useState("update");
  const isKickoff = type === "kickoff";
  const [option, setOption] = useState("since_last");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [endTouched, setEndTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const period = useMemo(() => computePeriod(option, { meetingDate, meetings, customStart, customEnd }), [option, meetingDate, meetings, customStart, customEnd]);
  const L = ({ children }) => <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: colors.textDim, margin: "12px 0 4px" }}>{children}</div>;

  // Spec §3.1: a custom period's end defaults to the meeting date.
  const choose = (value) => {
    setOption(value);
    if (value === "custom" && !endTouched) setCustomEnd(meetingDate);
  };
  const create = async () => {
    if (!meetingDate || (!isKickoff && !period.ok)) return;
    setBusy(true); setError(null);
    // A Kickoff has no update period (spec §3.1): it covers the meeting date itself.
    const fields = isKickoff
      ? { clientId, projectId, type: "kickoff", meetingDate, periodStart: meetingDate, periodEnd: meetingDate }
      : { clientId, projectId, type: "update", meetingDate, periodStart: period.start, periodEnd: period.end };
    const res = await api.meetings.create(fields);
    setBusy(false);
    if (res.ok) onCreated(res.data); else setError(res.data?.error || "Couldn’t create the meeting.");
  };

  return (
    <div role="presentation" onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div role="dialog" aria-modal="true" aria-label="New meeting" onClick={(e) => e.stopPropagation()} style={{ width: 460, maxWidth: "92vw", background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 14, padding: 24 }}>
        <h2 style={{ margin: "0 0 4px", fontSize: 18, color: colors.text }}>{isKickoff ? "New kickoff meeting" : "New update meeting"}</h2>
        <L>Meeting type</L>
        <div style={{ display: "flex", gap: 16 }}>
          {[{ value: "update", label: "Update" }, { value: "kickoff", label: "Kickoff" }].map((o) => (
            <label key={o.value} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: colors.text }}>
              <input type="radio" name="meeting-type" aria-label={o.label} checked={type === o.value} onChange={() => setType(o.value)} /> {o.label}
            </label>
          ))}
        </div>
        <L>Meeting date</L>
        <input aria-label="Meeting date" type="date" style={inputStyle} value={meetingDate} onChange={(e) => { const d = e.target.value; setMeetingDate(d); if (!endTouched) setCustomEnd(d); }} />
        {!isKickoff && (
          <>
            <L>Update period</L>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {PERIOD_OPTIONS.map((o) => (
                <label key={o.value} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: colors.text }}>
                  <input type="radio" name="period" aria-label={o.label} checked={option === o.value} onChange={() => choose(o.value)} /> {o.label}
                </label>
              ))}
            </div>
            {option === "custom" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
                <input aria-label="Period start" type="date" style={inputStyle} value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
                <input aria-label="Period end" type="date" style={inputStyle} value={customEnd} onChange={(e) => { setCustomEnd(e.target.value); setEndTouched(true); }} />
              </div>
            )}
            <div style={{ fontSize: 13, marginTop: 10, color: period.ok ? colors.textMuted : colors.danger }}>
              {period.ok ? `Covers ${short(period.start)} – ${short(period.end)}${period.fellBack ? " (no earlier meeting, so the last 2 weeks)" : ""}` : period.error}
            </div>
          </>
        )}
        {error && <div role="alert" style={{ color: colors.danger, fontSize: 13, marginTop: 8 }}>{error}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
          <button type="button" onClick={onClose} style={ghostBtn}>Cancel</button>
          <button type="button" onClick={create} disabled={(!isKickoff && !period.ok) || !meetingDate || busy} style={{ ...primaryBtn, opacity: (!isKickoff && !period.ok) || busy ? 0.5 : 1 }}>{busy ? "Creating…" : "Create meeting"}</button>
        </div>
      </div>
    </div>
  );
}
